import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import { ALWAYS_REFETCH, FIVE_MINUTES } from '@/constants/time';
import {
  getAskConversation,
  getAskConversations,
  getAskResult,
  type AskConversationPage,
} from '@/services/ask/api';
import type { AskError } from '@/services/ask/errors';

export type AskResultKey = {
  conversationId: string;
  turnId: string;
  source: string;
  resultId: string;
};

export const askKeys = {
  all: ['ask'] as const,
  lists: ['ask', 'conversations'] as const,
  list: (userId: string | undefined) => ['ask', 'conversations', userId] as const,
  snapshot: (conversationId: string) => ['ask', 'conversation', conversationId] as const,
  result: ({ conversationId, turnId, source, resultId }: AskResultKey) =>
    ['ask', 'result', conversationId, turnId, source, resultId] as const,
};

/**
 * Never retried on a `NOT_FOUND`: that is a final answer (deleted, or not the
 * reader's), and retrying only delays the screen saying so.
 */
function retryUnlessNotFound(failureCount: number, error: AskError): boolean {
  return error.kind !== 'NOT_FOUND' && failureCount < 2;
}

/**
 * The conversation snapshot, the one source of truth for a conversation. Always
 * stale, because every hint, focus and resume is an instruction to re-read it,
 * and it is cheap.
 */
export function getAskConversationV2QueryOptions(conversationId: string) {
  return queryOptions({
    queryKey: askKeys.snapshot(conversationId),
    queryFn: () => getAskConversation(conversationId),
    staleTime: ALWAYS_REFETCH,
    retry: retryUnlessNotFound,
  });
}

export function getAskConversationsV2QueryOptions(userId: string | undefined) {
  return infiniteQueryOptions({
    queryKey: askKeys.list(userId),
    queryFn: ({ pageParam }) => getAskConversations(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: AskConversationPage) => lastPage.nextCursor ?? undefined,
    staleTime: ALWAYS_REFETCH,
  });
}

/**
 * One result's detail. Cached for a few minutes: a redaction change inside that
 * window is acceptable, and reopening the conversation later reads it fresh.
 */
export function getAskResultV2QueryOptions(key: AskResultKey) {
  return queryOptions({
    queryKey: askKeys.result(key),
    queryFn: () => getAskResult(key),
    staleTime: FIVE_MINUTES,
    retry: retryUnlessNotFound,
  });
}

/**
 * Re-reads history from its first page only. An infinite query refetches every
 * loaded page in turn, so a reader scrolled five pages deep would otherwise
 * pay five sequential requests on every submit and every completed turn. What
 * changes (a new title, a conversation moving to the top) lands on page one;
 * the pages beyond it load again as the reader scrolls.
 */
export function refreshAskHistory(queryClient: QueryClient): void {
  queryClient.setQueriesData<InfiniteData<AskConversationPage, string | undefined>>(
    { queryKey: askKeys.lists },
    (data) =>
      data && data.pages.length > 1
        ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) }
        : data
  );

  queryClient.invalidateQueries({ queryKey: askKeys.lists }).catch(() => {});
}

/**
 * Forgets a conversation everywhere the app holds it: its snapshot, its result
 * details, and its row in every loaded history page. Used for a `404` and for a
 * delete, local or from another device, which the reader must not be able to
 * tell apart.
 */
export function forgetAskConversation(queryClient: QueryClient, conversationId: string): void {
  queryClient.removeQueries({ queryKey: askKeys.snapshot(conversationId), exact: true });

  queryClient.removeQueries({ queryKey: ['ask', 'result', conversationId] });

  queryClient.setQueriesData<InfiniteData<AskConversationPage, string | undefined>>(
    { queryKey: askKeys.lists },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          conversations: page.conversations.filter(
            (conversation) => conversation.id !== conversationId
          ),
        })),
      }
  );
}
